import Button from 'components/base/Button';
import Dropzone from 'components/base/Dropzone';
import Section from 'components/base/Section';
import TinymceEditor from 'components/base/TinymceEditor';
import OrganizeFormCard from 'components/cards/OrganizeFormCard';
import InventoryTab from 'components/tabs/InventoryTab';
import { Col, Form, Row, Spinner } from 'react-bootstrap';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { addProductSchema, AddProductSchema } from './model';
import { useState } from 'react';
import {
  addProduct,
  ProductBulkOf,
  ProductSpc,
  ProductVariant,
  VariantType
} from 'api/products/_requests';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { uploadMultipleFiles, uploadFile } from 'api/upload/_requests';
import { useSelector } from 'react-redux';
import { RootState } from 'redux-state/store';
import ProductVariantForm, { SelectOption } from './ProductVariantTypeForm';
import { Plus } from 'feather-icons-react';
import ProductVariantCard from './ProductVariantCard';
import ProductSpecifications from './ProductSpecifications';
import ProductIntheBox from './ProductIntheBox';
import ProductBulkOffer from './ProductBulkOffer';
import CouponAddInProduct from './CouponAddInProduct';

const AddProduct = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state?.auth);
  const [variantTypes, setVariantTypes] = useState<VariantType[]>([]);
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [generatingVariants, setGeneratingVariants] = useState(false);
  const [selectedColors, setSelectedColors] = useState<SelectOption[]>([]);
  const [selectedSize, setSelectedSize] = useState<SelectOption[]>([]);
  const [selectedWeight, setSelectedWeight] = useState<SelectOption[]>([]);
  const [variantGroupType, setVariantGroupType] = useState<string>('');
  const [checkedVariants, setCheckedVariants] = useState<{
    [key: number]: boolean;
  }>({});
  const [specification, setSpecification] = useState<ProductSpc[]>([
    { header: '', value: '' }
  ]);
  const [bulkOffer, setBulkOffer] = useState<ProductBulkOf[]>([
    { quantity: 0, price: 0 }
  ]);
  const [inbox, setInbox] = useState<string[]>(['']);
  const methods = useForm<AddProductSchema>({
    resolver: zodResolver(addProductSchema),
    defaultValues: {
      tags: []
    }
  });

  const handleImageDrop = (acceptedFiles: File[]) => {
    setPendingFiles(prev => [...prev, ...acceptedFiles]);
    const tempUrls = acceptedFiles.map(file => URL.createObjectURL(file));
    setUploadedImages(prev => [...prev, ...tempUrls]);
  };

  const handleThumbnailDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setThumbnail(file);
    }
  };

  const uploadImages = async (files: File[]) => {
    const currentTimestamp = new Date().getTime();
    const fileNames: string[] = [];
    const formData = new FormData();
    files.forEach(file => {
      formData.append('image', file);
      fileNames.push(currentTimestamp + '_' + file.name);
    });
    formData.append('timestamp', currentTimestamp.toString());

    const response = await uploadMultipleFiles(formData);
    return fileNames;
  };

  const uploadThumbnail = async (file: File) => {
    const currentTimestamp = new Date().getTime();
    const imageName = currentTimestamp + '_' + file.name;
    const formData = new FormData();
    formData.append('image', file);
    formData.append('name', imageName);

    const response = await uploadFile(formData);
    return imageName;
  };

  const onSubmit = async (data: AddProductSchema) => {
    try {
      if (!thumbnail) {
        toast.error('Thumbnail is required');
        return;
      }

      setIsSubmitting(true);

      // Upload thumbnail first
      let thumbnailUrl = '';
      if (thumbnail) {
        thumbnailUrl = await uploadThumbnail(thumbnail);
      }

      // Upload images
      // if (pendingFiles.length > 0) {
      //   const uploadedUrls = await uploadImages(pendingFiles);
      //   data.images = uploadedUrls;
      // }

      const productVariants = variants
        .filter((variant, idx) => checkedVariants[idx])
        .map(variant => ({
          ...variant,
          images: [thumbnailUrl]
        }));

      if (productVariants.length === 0) {
        toast.error('Product must have at least one variant');
        setIsSubmitting(false);
        return;
      }

      const product = {
        ...data,
        price: 0,
        inBox: inbox,
        bulkOffer: bulkOffer,
        specification: specification,
        discountedPrice: 0,
        variantTypes,
        vendorId: user?._id || '',
        _id: '',
        inStock: true,
        tags: data.tags || [],
        images: [],
        thumbnail: thumbnailUrl,
        productVariants,
        industry: null
      };
      // Create product with uploaded image URLs
      await addProduct(product).then(res => {
        toast.success('Product added successfully');
        navigate(`/product-details/${res?.slug}`);
      });
    } catch (error) {
      toast.error('Failed to add product');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDiscard = () => {
    methods.reset();
    setUploadedImages([]);
    setPendingFiles([]);
    setThumbnail(null);
  };

  const handleAddVariant = () => {
    if (variantTypes.length < 3) {
      const availableTypes: VariantType[] = ['color', 'size', 'weight'].filter(
        type => !variantTypes.includes(type as VariantType)
      ) as VariantType[];
      if (availableTypes.length > 0) {
        setVariantTypes(prev => [...prev, availableTypes[0]]);
      }
    }
  };

  const handleGenerateVariants = () => {
    setGeneratingVariants(true);

    // Create variants of all possible combinations based on selected variant types
    let combinations: ProductVariant[] = [];

    // Helper function to create a variant object
    const createVariant = (
      color = '',
      size = '',
      weight = ''
    ): ProductVariant => ({
      color,
      size,
      weight,
      price: 0,
      discountedPrice: 0,
      stockQuantity: 0,
      inStock: true,
      images: []
    });

    // Handle single variant type
    if (variantTypes.length === 1) {
      const type = variantTypes[0];
      const options =
        type === 'color'
          ? selectedColors
          : type === 'size'
          ? selectedSize
          : selectedWeight;

      combinations = options.map(opt =>
        createVariant(
          type === 'color' ? opt.value : '',
          type === 'size' ? opt.value : '',
          type === 'weight' ? opt.value : ''
        )
      );
    }

    // Handle two variant types
    else if (variantTypes.length === 2) {
      const [type1, type2] = variantTypes;
      const options1 =
        type1 === 'color'
          ? selectedColors
          : type1 === 'size'
          ? selectedSize
          : selectedWeight;
      const options2 =
        type2 === 'color'
          ? selectedColors
          : type2 === 'size'
          ? selectedSize
          : selectedWeight;

      combinations = options1.flatMap(opt1 =>
        options2.map(opt2 =>
          createVariant(
            type1 === 'color'
              ? opt1.value
              : type2 === 'color'
              ? opt2.value
              : '',
            type1 === 'size' ? opt1.value : type2 === 'size' ? opt2.value : '',
            type1 === 'weight'
              ? opt1.value
              : type2 === 'weight'
              ? opt2.value
              : ''
          )
        )
      );
    }

    // Handle three variant types
    else if (variantTypes.length === 3) {
      combinations = selectedColors.flatMap(color =>
        selectedSize.flatMap(size =>
          selectedWeight.map(weight =>
            createVariant(color.value, size.value, weight.value)
          )
        )
      );
    }

    setVariants(combinations);
    setVariantGroupType(variantTypes[0]);
    setGeneratingVariants(false);
  };

  return (
    <Section>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className="mb-9">
          <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
            <div>
              <h2 className="mb-2">Add a product</h2>
              <h5 className="text-body-tertiary fw-semibold">
                Add a new product to your store
              </h5>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="phoenix-secondary"
                type="button"
                onClick={handleDiscard}
              >
                Discard
              </Button>
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Publishing...' : 'Publish product'}
              </Button>
            </div>
          </div>
          <Row className="g-5">
            <Col xs={12} xl={8}>
              <div className="px-3 py-2 border border-2 border-secondary-subtle rounded">
                <div className="mb-4">
                  <h4 className="mb-3">Product Name</h4>
                  <Form.Control
                    {...methods.register('name')}
                    placeholder="Write title here..."
                    className={
                      methods.formState.errors.name ? 'is-invalid' : ''
                    }
                  />
                  {methods.formState.errors.name && (
                    <Form.Control.Feedback type="invalid">
                      {methods.formState.errors.name.message}
                    </Form.Control.Feedback>
                  )}
                </div>
                <div className="mb-4">
                  <h4 className="mb-3">Product Description</h4>
                  <Controller
                    name="description"
                    control={methods.control}
                    render={({ field }) => (
                      <TinymceEditor
                        value={field.value}
                        onChange={field.onChange}
                        options={{
                          height: '15rem',
                          placeholder: 'Write a description here...'
                        }}
                      />
                    )}
                  />
                  {methods.formState.errors.description && (
                    <div className="text-danger mt-1">
                      {methods.formState.errors.description.message}
                    </div>
                  )}
                </div>
                <div className="mb-2">
                  <h4 className="mb-3">Key Features</h4>
                  <Controller
                    name="keyfeature"
                    control={methods.control}
                    render={({ field }) => (
                      <TinymceEditor
                        value={field.value}
                        onChange={field.onChange}
                        options={{
                          height: '12rem',
                          placeholder: 'Write a key features here...'
                        }}
                      />
                    )}
                  />
                  {methods.formState.errors.keyfeature && (
                    <div className="text-danger mt-1">
                      {methods.formState.errors.keyfeature.message}
                    </div>
                  )}
                </div>
              </div>
              <div className="mb-5 mt-4 px-3 py-2 border border-2 border-secondary-subtle rounded">
                <h4 className="mb-3">Thumbnail</h4>
                <Controller
                  name="thumbnail"
                  control={methods.control}
                  render={({ field }) => (
                    <Dropzone
                      className="mb-3"
                      accept={{
                        'image/*': ['.png', '.jpeg', '.jpg']
                      }}
                      onDrop={handleThumbnailDrop}
                      multiple={false}
                    />
                  )}
                />
                {/* {thumbnailPreview && (
                  <div className="position-relative d-inline-block">
                    <img
                      src={thumbnailPreview}
                      alt="Thumbnail preview"
                      className="rounded"
                      style={{
                        width: '200px',
                        height: '200px',
                        objectFit: 'cover'
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                      onClick={removeThumbnail}
                    >
                      ×
                    </button>
                  </div>
                )} */}
                {methods.formState.errors.thumbnail && (
                  <Form.Control.Feedback type="invalid">
                    {methods.formState.errors.thumbnail?.message}
                  </Form.Control.Feedback>
                )}
              </div>
              <div className="py-2 px-3 border border-2 border-secondary-subtle rounded mt-4">
                <div className="mb-5">
                  <h4 className="mb-3">Product Specification</h4>
                  <ProductSpecifications
                    specification={specification}
                    setSpecification={setSpecification}
                  />
                </div>
                <div className="mb-5">
                  <h4 className="mb-3">In The Box</h4>
                  <ProductIntheBox inbox={inbox} setInbox={setInbox} />
                </div>
                <div className="mb-5">
                  <h4 className="mb-3">Bulk Offer</h4>
                  <ProductBulkOffer
                    bulkOffer={bulkOffer}
                    setBulkOffer={setBulkOffer}
                  />
                </div>
                <div className="mb-2">
                  <h4 className="mb-3">Add Coupons</h4>
                  <CouponAddInProduct vendorId={user?._id ?? ''} />
                </div>
              </div>
            </Col>
            <Col xs={12} xl={4}>
              <Row className="g-2 border border-2 border-secondary-subtle rounded py-2 px-3 mt-0">
                <Col xs={12} xl={12}>
                  <OrganizeFormCard className="mb-3" />
                </Col>
                {/* <Col xs={12} xl={12}>
                  <VariantFormCard />
                </Col> */}
              </Row>
            </Col>
          </Row>
          <div className="mt-4 border border-2 border-secondary-subtle rounded py-2 px-3">
            <div className="mb-5 ">
              <h4 className="mb-3">Variant Types</h4>
              <ProductVariantForm
                variants={variants}
                setVariants={setVariants}
                variantTypes={variantTypes}
                setVariantTypes={setVariantTypes}
                selectedColors={selectedColors}
                setSelectedColors={setSelectedColors}
                selectedSize={selectedSize}
                setSelectedSize={setSelectedSize}
                selectedWeight={selectedWeight}
                setSelectedWeight={setSelectedWeight}
              />

              {variantTypes.length < 3 && (
                <Button variant="primary" onClick={handleAddVariant}>
                  Add variant
                </Button>
              )}
            </div>

            <div className="mb-5">
              {variantTypes.length > 0 && <h4 className="mb-3">Variants</h4>}
              {variantTypes.length > 0 && (
                <>
                  {variants.length === 0 ? (
                    <p
                      className="text-primary cursor-pointer text-decoration-underline"
                      onClick={handleGenerateVariants}
                    >
                      <Plus size={12} />
                      Generate variants
                    </p>
                  ) : (
                    <ProductVariantCard
                      variantGroupType={variantGroupType}
                      setVariantGroupType={setVariantGroupType}
                      variantGroupValues={variantTypes}
                      variants={variants}
                      setVariants={setVariants}
                      checkedVariants={checkedVariants}
                      setCheckedVariants={setCheckedVariants}
                    />
                  )}
                </>
              )}
              {generatingVariants && (
                <div className="d-flex justify-content-center h-100 align-items-center">
                  <Spinner animation="grow" variant="primary" />
                </div>
              )}
            </div>
          </div>
          <div>
            <h4 className="mb-3 mt-4">Inventory</h4>
            <InventoryTab />
          </div>
        </form>
      </FormProvider>
    </Section>
  );
};

export default AddProduct;
